import {
	PlayerHookType, RequestHookContextInterface, RequestObjectType, RequestOptionsInterface
} from '@cbsinteractive/avia-js';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { AbstractAviaPlayerHook } from 'tag/ts/tag/player/adapters/avia/hooks/AbstractAviaPlayerHook';

// avia-js's RecoveryEnabledRequest.shouldRetry() gates purely on attempt count, with no awareness
// of error type -- these stay small so a blind retry of a deterministic rejection (bad token,
// expired cert) only costs bounded extra latency, not a long stall (PLBCK-8219).
const DEFAULT_RETRY_ATTEMPTS = 2;
const DEFAULT_RETRY_INTERVALS = [ 500, 1500 ];

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description FairPlay DRM key request retry resilience hook
 */
export class AviaPlayerFairPlayRetryHook extends AbstractAviaPlayerHook {

	hookType: PlayerHookType = PlayerHookType.REQUEST;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'AviaPlayerFairPlayRetryHook'
			}
		);

		this.tagEventsHandler( this.#onTagEvent, [
			TagEvent.ON_CREATE_PLAYER_SUCCESS
		] );
	}

	/**
	 * @description Tag application event handler
	 * @param {TagEventVO} event
	 */
	#onTagEvent = ( event: TagEventVO ): void => {
		// filter by event type
		switch ( event?.type ) {
			case TagEvent.ON_CREATE_PLAYER_SUCCESS:
				// stop listening to this event... we need it once per instance to prevent memory leak
				this.removeTagEventsHandler(
					this.#onTagEvent,
					this.tag.eventDispatcher,
					[ TagEvent.ON_CREATE_PLAYER_SUCCESS ]
				).then(
					() => {
						this.registerHook();
					}
				);
				break;
		}
	};

	/**
	 * @description Callback passed to player's registerHook function. Injects error-recovery
	 * retry config into FairPlay DRM key requests (app cert + license exchange) only; manifest
	 * and segment requests are untouched, since those already have their own Shaka/HLS.js retry config.
	 * @param {RequestHookContextInterface} context
	 */
	callback = ( context: RequestHookContextInterface ): void => {
		if ( !this.#isKeyRequest( context ) ) {
			return;
		}

		const options: RequestOptionsInterface = context.value;

		// don't clobber a value some other hook already set on this request
		if ( !this.utils.Type.isNullOrUndefined( options.errorRecovery ) ) {
			return;
		}

		this?.logger?.log( 'FairPlay key request missing errorRecovery, injecting retry config', 'url:', options.url );

		options.errorRecovery = {
			retryAttempts: this.#retryAttempts,
			retryIntervals: this.#retryIntervals
		};
	};

	#isKeyRequest = ( context: RequestHookContextInterface ): boolean => {
		return context?.metadata?.objectType === RequestObjectType.KEY;
	};

	get #retryAttempts(): number {
		const configured = this.tag?.model?.config?.tag?.Hooks?.params?.FairPlayRetryHook?.retryAttempts;
		return this.utils.Type.isInteger( configured ) && configured >= 0 ? configured : DEFAULT_RETRY_ATTEMPTS;
	}

	get #retryIntervals(): number[] {
		const configured = this.tag?.model?.config?.tag?.Hooks?.params?.FairPlayRetryHook?.retryIntervals;
		return this.utils.Type.isArray( configured ) && !this.utils.Type.isEmpty( configured ) ? configured : DEFAULT_RETRY_INTERVALS;
	}
}